import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { PARTNER_BRANDS, SOLUTIONS } from "../data/content";

export default function SolutionsPage() {
  const { hash } = useLocation();
  const partnerLogoMap = PARTNER_BRANDS.reduce((acc, brand) => {
    acc[brand.name.toLowerCase()] = brand.logo;
    return acc;
  }, {});

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      }
    }
  }, [hash]);

  return (
    <main data-testid="solutions-page" className="bg-slate-950 text-slate-100 min-h-screen">
      <PageHeader
        eyebrow="/ Solutions"
        title={
          <>
            Six core systems.
            <br />
            <span className="text-slate-500">One unified architecture.</span>
          </>
        }
        subtitle="From single-camera installs to enterprise-grade command centers — every solution is designed, deployed and maintained as a connected ecosystem."
        image="https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg"
      />

      {/* Index strip */}
      <section className="border-b border-white/5 bg-slate-950 sticky top-16 md:top-20 z-30 backdrop-blur-2xl bg-slate-950/85">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex gap-6 overflow-x-auto scrollbar-none">
          {SOLUTIONS.map((s, i) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`solutions-index-${s.id}`}
              className="text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan-300 font-mono whitespace-nowrap py-1"
            >
              <span className="text-cyan-400/70 mr-2">0{i + 1}</span>
              {s.title}
            </a>
          ))}
        </div>
      </section>

      {/* Solution blocks */}
      <div className="divide-y divide-white/5">
        {SOLUTIONS.map((s, idx) => {
          const Icon = s.icon;
          const reversed = idx % 2 === 1;
          return (
            <section
              key={s.id}
              id={s.id}
              data-testid={`solution-block-${s.id}`}
              className="py-20 md:py-28 px-6 md:px-10 scroll-mt-32"
            >
              <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
                <div
                  className={`lg:col-span-6 ${
                    reversed ? "lg:order-2" : ""
                  }`}
                >
                  <div className="relative aspect-[4/3] border border-white/10 bg-slate-900 overflow-hidden tracing-card">
                    {s.image && (
                      <>
                        <img
                          src={s.image}
                          alt={s.title}
                          className="w-full h-full object-cover opacity-90"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
                      </>
                    )}
                    <div className="absolute top-4 left-4 h-12 w-12 border border-cyan-400/40 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.6} />
                    </div>
                    <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-mono bg-slate-950/70 backdrop-blur-sm px-3 py-1.5">
                      0{idx + 1} · {s.subtitle}
                    </div>
                  </div>
                </div>

                <div
                  className={`lg:col-span-6 ${
                    reversed ? "lg:order-1" : ""
                  }`}
                >
                  <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-3">
                    {s.subtitle}
                  </div>
                  <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-50 mb-5">
                    {s.title}
                  </h2>
                  <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-8">
                    {s.description}
                  </p>

                  <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-4">
                    What's included
                  </div>
                  <ul className="space-y-3 mb-8">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm md:text-base text-slate-300">
                        <Check className="h-4 w-4 text-cyan-400 mt-1 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="border-t border-white/5 pt-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-3">
                      Brand Partners
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {s.partners.map((p) => {
                        const partnerLogo = partnerLogoMap[p.toLowerCase()];

                        return (
                          <div
                            key={p}
                            className="flex h-9 items-center justify-center rounded-full border border-white/10 bg-slate-900/80 px-3 py-1.5 text-slate-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
                          >
                            {partnerLogo ? (
                              <img
                                src={partnerLogo}
                                alt={p}
                                className="h-4 md:h-5 w-auto max-w-[90px] object-contain"
                              />
                            ) : (
                              <span className="text-[11px] font-mono uppercase tracking-wider">
                                {p}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="py-20 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-2">
              / Custom Architecture
            </div>
            <h3 className="text-2xl md:text-3xl tracking-tight font-medium text-slate-100">
              Need a tailored deployment? We design end-to-end.
            </h3>
          </div>
          <Link to="/contact">
            <Button className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-12 px-7 btn-glow group">
              Talk to an Engineer
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
